import {toast} from 'react-toastify';
import {showCommunicationError, showSuccess} from 'helpers/alerts';
import {t} from 'i18next';
import {action, makeObservable, observable, runInAction} from 'mobx';
import {redirectTo} from 'routing/browserHistory';
import {AppRoutePaths, AuthRoutePaths} from 'routing/Routes';
import {SignUpStatusCode} from 'views/auth/EmailCheckView';
import {
  ApiUserAuthSendpasswordtokenPostRequest,
  CollectorAdministratorCreateDto,
  GarageDto,
  LoginLocalRequest,
  RegisterUserLocalRequest,
  ResetPasswordRequest,
} from '../../../../PRMGApiClient';
import {Api} from '../../../../PRMGApiClient/ApiClient';
import {AuthState} from './auth/AuthState';
import {AppCommonData} from './AppCommonData';
import {CalendarBoxState} from 'views/calendar/calendarBox/CalendarBoxState';

export class AppStore extends AppCommonData {
  authState: AuthState = new AuthState();
  calendarBoxState: CalendarBoxState = new CalendarBoxState();

  @observable
  isLoggedIn: boolean = false;

  constructor() {
    super();
    makeObservable(this);
  }

  initStates(): void {
    this.isLoggedIn = true;
  }

  updateGarageData(garageId: number) {
    if (this.commonData?.garages) {
      const garageIndex = this.commonData.garages.findIndex(({id}) => garageId === id);
      Api.GarageApi.apiGarageGetIdGet({id: garageId}).then((res) => {
        runInAction(() => {
          if (this.commonData && this.commonData.garages && res.content) {
            garageIndex === -1
              ? this.commonData.garages.push(res.content)
              : (this.commonData.garages[garageIndex] = res.content);
          }
        });
      });
    }
  }

  @action
  deleteGarageData(garage: GarageDto) {
    if (this.commonData?.garages) {
      this.commonData.garages = this.commonData.garages.filter(({id}) => id !== garage.id);
    }
  }

  @action
  login(payload: LoginLocalRequest) {
    this.isLoading = true;
    this.authState
      .login(payload)
      .then((res) => {
        if (res?.statusCode === SignUpStatusCode.ConfirmationPending) {
          runInAction(() => {
            this.authState.signUpEmail = this.authState.loginEmail;
            this.authState.signUpStatusCode = SignUpStatusCode.ConfirmationPending;
          });
          redirectTo(AuthRoutePaths.emailCheck.url);
          return;
        }

        this.getCommonData().catch(showCommunicationError);
      })
      .catch((err) => {
        if (err.status === 400 || err.status === 401) {
          toast.error(t('Wrong email or password'), {className: 'skip-fade'});
        } else {
          showCommunicationError(err);
        }
      })
      .finally(() => (this.isLoading = false));
  }

  logout(): void {
    this.authState
      .logout()
      .then(() => {
        this.isLoggedIn = false;
      })
      .catch((err) => {
        showCommunicationError(err);
      });
  }

  @action
  signUp(payload: RegisterUserLocalRequest): void {
    this.isLoading = true;
    this.authState
      .signUp(payload)
      .then((res) => {
        runInAction(() => {
          this.authState.signUpStatusCode = res?.statusCode ?? 0;
        });
        redirectTo(AuthRoutePaths.emailCheck.url);
      })
      .catch(async (err) => {
        const response = await err.json();
        if (err.status === 400 && response.statusCode === SignUpStatusCode.UsernameAlreadyTaken) {
          toast.error(t('Email is already taken'), {className: 'skip-fade'});
        }
      })
      .finally(() => (this.isLoading = false));
  }

  resetPassword(payload: ResetPasswordRequest): void {
    this.authState
      .reset({resetPasswordRequest: payload})
      .then(() => {
        showSuccess(t('Password has been changed'));
        redirectTo(AuthRoutePaths.signIn.url);
      })
      .catch(showCommunicationError);
  }

  sendResetLink(payload: ApiUserAuthSendpasswordtokenPostRequest): void {
    this.authState
      .sendResetLink(payload)
      .then(() => {
        redirectTo(AuthRoutePaths.emailCheckResetLink.url);
      })
      .catch((err) => {
        if (err.status === 404) {
          toast.error(t('Email is not registered'), {className: 'skip-fade'});
        }
      });
  }

  async register(payload: CollectorAdministratorCreateDto): Promise<void> {
    this.authState
      .createCollectorAdministrator(payload)
      .then(() => {
        this.getCommonData().then(() => {
          redirectTo(AppRoutePaths.dashboard.url);
        });
      })
      .catch(showCommunicationError);
  }
}

export const appState = new AppStore();
